import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
// framer-motion removed from the layout critical path. The services dropdown
// and mobile menu now use CSS transitions defined in index.css (.tn-pop,
// .tn-mobile-menu). Keeps the navbar identical-looking but drops ~110KB of
// JS off the homepage's initial-load critical path.

const SERVICES = [
  { title: "Kitchen Remodeling", slug: "kitchen" },
  { title: "Bathroom Remodeling", slug: "bathroom" },
  { title: "Basement Finishing", slug: "basement" },
  { title: "Whole-House Renovation", slug: "whole-house" },
  { title: "Home Additions", slug: "additions" },
  { title: "Decks & Exterior", slug: "exterior" },
  { title: "ADU & In-Law Suites", slug: "adu" },
  { title: "Outdoor Living", slug: "outdoor-living" },
  { title: "Historic Restoration", slug: "historic-restoration" },
  { title: "Aging-in-Place", slug: "aging-in-place" },
];

const NAV_LINKS = [
  { to: "/portfolio", label: "Projects" },
  { to: "/areas", label: "Areas" },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setServicesOpen(false);
  }, [location]);

  return (
    <header className={`sticky top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? "bg-white shadow-lg" : "bg-white shadow-sm"}`}>
      {/* Top bar */}
      <div className="bg-primary text-white py-2 px-4 text-center text-xs font-body tracking-wide hidden md:block">
        MHIC# 05-147079 · Licensed &amp; Insured · Serving Baltimore &amp; Surrounding Areas
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24 md:h-28">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img
              src="https://images.squarespace-cdn.com/content/v1/656a896e54775d222948c688/532ef95c-0298-43a0-938b-bd03a6120663/c4af02ac-7a65-4df3-ae24-b2c387ce173a.png?format=300w"
              alt="Top-Notch Contractors"
              className="h-20 md:h-24 w-auto"
              width="280"
              height="240"
              decoding="async"
            />
          </Link>

          {/* Desktop Navigation */}
          {!isMobile && (
            <nav className="hidden md:flex items-center gap-1">
              {/* Services Dropdown */}
              <div className="relative" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)}>
                <button className={`px-4 py-2 text-base font-semibold font-body rounded transition-colors flex items-center gap-1 ${location.pathname.startsWith("/services") ? "text-accent" : "text-foreground hover:text-accent"}`}>
                  Services <ChevronDown className="w-4 h-4" />
                </button>
                {servicesOpen && (
                  <div
                    className="tn-pop absolute top-full left-0 w-56 bg-white border border-border rounded-lg shadow-xl py-2 mt-1"
                  >
                    <Link to="/services" className="block px-4 py-2 text-sm font-body font-semibold text-primary hover:text-accent hover:bg-secondary transition-colors border-b border-border mb-1">
                      All Services
                    </Link>
                    {SERVICES.map((s) => (
                      <Link key={s.slug} to={`/services/${s.slug}`} className="block px-4 py-2 text-sm font-body text-foreground hover:text-accent hover:bg-secondary transition-colors">
                        {s.title}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {NAV_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-4 py-2 text-base font-body font-semibold rounded transition-colors ${
                    location.pathname.startsWith(link.to)
                      ? "text-accent"
                      : "text-foreground hover:text-accent"
                  }`}
                >
                  {link.label}
                </Link>
              ))}

              <a href="tel:4105989933" className="ml-2">
                <Button size="sm" variant="outline" className="border-primary/30 text-primary hover:bg-primary hover:text-white font-body text-sm transition-colors">
                  <Phone className="w-3.5 h-3.5 mr-1.5" />
                  (410) 598-9933
                </Button>
              </a>
              <Link to="/contact" className="ml-2">
                <Button size="sm" className="bg-accent text-white hover:bg-accent/90 font-body font-semibold text-sm">
                  Free Consultation
                </Button>
              </Link>
            </nav>
          )}

          {/* Mobile controls */}
          {isMobile && (
            <div className="flex items-center gap-2">
              <a href="tel:4105989933" aria-label="Call (410) 598-9933">
                <Button size="sm" className="bg-accent text-white hover:bg-accent/90">
                  <Phone className="w-4 h-4" />
                </Button>
              </a>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMobileMenuOpen}
                className="p-2 text-foreground hover:bg-secondary rounded-lg transition-colors min-w-[44px] min-h-[44px]"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          className="tn-mobile-menu fixed inset-0 md:hidden bg-white z-50 overflow-y-auto"
        >
            <div className="p-5">
              <div className="flex items-center justify-between mb-8">
                <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
                  <img
                    src="https://images.squarespace-cdn.com/content/v1/656a896e54775d222948c688/532ef95c-0298-43a0-938b-bd03a6120663/c4af02ac-7a65-4df3-ae24-b2c387ce173a.png?format=300w"
                    alt="Top-Notch Contractors"
                    className="h-12 w-auto"
                    width="140"
                    height="120"
                    decoding="async"
                  />
                </Link>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  aria-label="Close menu"
                  className="p-2 text-foreground hover:bg-secondary rounded-lg min-w-[44px] min-h-[44px]"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="space-y-1">
                <Link to="/services" onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-3 font-body font-semibold text-primary rounded-lg hover:bg-secondary">
                  Services
                </Link>
                {SERVICES.map((s) => (
                  <Link key={s.slug} to={`/services/${s.slug}`} onClick={() => setIsMobileMenuOpen(false)} className="block px-8 py-2.5 font-body text-sm text-muted-foreground rounded-lg hover:bg-secondary hover:text-foreground">
                    {s.title}
                  </Link>
                ))}
                {NAV_LINKS.map((link) => (
                  <Link key={link.to} to={link.to} onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-3 font-body font-medium text-foreground rounded-lg hover:bg-secondary">
                    {link.label}
                  </Link>
                ))}
              </div>
              <div className="mt-8 space-y-3">
                <a href="tel:4105989933" className="block">
                  <Button variant="outline" className="w-full border-primary text-primary font-body font-semibold py-6 text-base">
                    <Phone className="w-5 h-5 mr-2" /> (410) 598-9933
                  </Button>
                </a>
                <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button className="w-full bg-accent text-white hover:bg-accent/90 font-body font-semibold py-6 text-base">
                    Free Consultation
                  </Button>
                </Link>
              </div>
              <p className="mt-6 text-center text-xs text-muted-foreground font-body">MHIC# 05-147079 · Licensed &amp; Insured</p>
            </div>
        </div>
      )}
    </header>
  );
}